import axios from 'axios';
import brainwaveSimulator from './brainwave-simulator';
import config from '@/config';
import logger from '@/utils/logger';
import { parseApiResponse, handleApiError, snakeToCamel } from '@/utils/apiResponse';

/**
 * 实时状态查询API服务
 */
class RealtimeAPI {
  constructor() {
    this.lastQueryTimestamp = 0;
  }

  /**
   * 获取当日预约记录
   * @param {string} storeId - 门店ID
   * @returns {Promise} 返回当日预约记录数据
   */
  async getTodayAppointments(storeId) {
    try {
      const url = config.getApiUrl(config.apiEndpoints.appointments.today, storeId);
      logger.debug('[当日预约] getTodayAppointments 请求', url);
      const response = await axios.get(url);
      
      // 使用统一的响应解析函数处理 ApiResponse 格式
      const result = parseApiResponse(response);
      
      // 检查业务状态码（特殊处理：1001 表示无数据）
      if (result.code === 1001) {
        return {
          success: true,
          data: [],
          message: result.message || '今日暂无预约记录',
          timestamp: Date.now()
        };
      }
      
      if (result.success && result.data) {
        // 将下划线字段名转换为驼峰命名
        const appointmentData = Array.isArray(result.data) 
          ? result.data.map(item => snakeToCamel(item))
          : [];
        
        return {
          success: true,
          data: appointmentData,
          message: result.message,
          timestamp: Date.now()
        };
      }
      
      // 兼容其他格式
      return {
        success: true,
        data: Array.isArray(result.data) ? result.data.map(item => snakeToCamel(item)) : [],
        timestamp: Date.now()
      };
      
    } catch (error) {
      // 如果是HTTP错误响应，也检查是否包含业务状态码1001
      if (error.response && error.response.data && error.response.data.code === 1001) {
        return {
          success: true,
          data: [],
          message: error.response.data.message || '今日暂无预约记录',
          timestamp: Date.now()
        };
      }
      
      const errorResult = handleApiError(error);
      return {
        success: false,
        error: errorResult.message || this.formatErrorInfo(error),
        data: null,
        timestamp: Date.now()
      };
    }
  }

  /**
   * 获取实时状态数据
   * @param {Array} roomIds - 房间ID列表
   * @param {number} lastQueryTimestamp - 上次查询时间戳（可选，默认使用内部维护的时间戳）
   * @returns {Promise} 返回实时状态数据
   */
  async getRealtimeStatus(roomIds, lastQueryTimestamp = null) {
    try {
      // 如果没有传入时间戳，使用内部维护的时间戳
      const timestamp = lastQueryTimestamp !== null ? lastQueryTimestamp : this.lastQueryTimestamp;
      
      // 构建查询参数
      const params = new URLSearchParams();
      
      // 添加房间ID参数（支持多个房间ID）
      if (Array.isArray(roomIds)) {
        roomIds.forEach(roomId => {
          params.append('room_ids', roomId);
        });
      } else if (roomIds) {
        params.append('room_ids', roomIds);
      }
      
      // 添加时间戳参数
      params.append('last_query_timestamp', timestamp);
      
      const apiUrl = `${config.getRealtimeApiUrl(config.apiEndpoints.realtime.status)}?${params.toString()}`;
      
      
      const response = await axios.get(apiUrl);
      
      // 更新内部时间戳为当前时间
      this.lastQueryTimestamp = Date.now() / 1000;
      
      
      return response.data;
    } catch (error) {
      // 友好的错误处理，避免控制台红色报错
      const errorInfo = this.formatErrorInfo(error);
      
      // 使用友好的日志输出
      
      // 返回标准化的错误响应，而不是抛出异常
      return {
        success: false,
        status: 'error',
        message: errorInfo.message,
        code: errorInfo.code,
        data: null,
        timestamp: Date.now() / 1000
      };
    }
  }

  /**
   * 格式化错误信息为友好的提示
   * @param {Error} error - 原始错误对象
   * @returns {Object} 格式化后的错误信息
   */
  formatErrorInfo(error) {
    let message = '网络连接异常';
    let code = 'NETWORK_ERROR';
    
    if (error.code === 'ECONNREFUSED') {
      message = '服务器连接被拒绝，可能服务未启动';
      code = 'CONNECTION_REFUSED';
    } else if (error.code === 'ENOTFOUND') {
      message = '无法解析服务器地址';
      code = 'DNS_ERROR';
    } else if (error.code === 'ETIMEDOUT') {
      message = '请求超时，服务器响应缓慢';
      code = 'TIMEOUT';
    } else if (error.response) {
      // 服务器返回了错误状态码
      const status = error.response.status;
      if (status === 404) {
        message = '请求的接口不存在';
        code = 'NOT_FOUND';
      } else if (status === 500) {
        message = '服务器内部错误';
        code = 'SERVER_ERROR';
      } else if (status >= 400 && status < 500) {
        message = '请求参数错误';
        code = 'CLIENT_ERROR';
      } else {
        message = `服务器响应异常 (${status})`;
        code = 'HTTP_ERROR';
      }
    } else if (error.message) {
      message = error.message;
    }
    
    return { message, code };
  }

  /**
   * 重置查询时间戳
   */
  resetTimestamp() {
    this.lastQueryTimestamp = 0;
  }

  /**
   * 获取当前时间戳
   * @returns {number} 当前Unix时间戳
   */
  getCurrentTimestamp() {
    return Date.now() / 1000;
  }

  /**
   * 从房间名称提取房间ID
   * @param {string} roomName - 房间名称，如"房间1"、"Room 1"等
   * @returns {string} 房间ID，如"room_1"
   */
  extractRoomId(roomName) {
    if (!roomName) return null;
    
    // 提取房间名称中的数字
    const match = roomName.match(/(\d+)/);
    if (match) {
      return `room_${match[1]}`;
    }
    
    // 如果没有数字，直接使用房间名称
    return `room_${roomName.toLowerCase().replace(/\s+/g, '_')}`;
  }

  /**
   * 从预约数据中提取房间ID列表
   * @param {Array} appointmentData - 预约数据数组
   * @returns {Array} 房间ID列表
   */
  extractRoomIds(appointmentData) {
    if (!Array.isArray(appointmentData)) return [];
    
    return appointmentData
      .filter(item => item && item.roomName)
      .map(item => this.extractRoomId(item.roomName))
      .filter(roomId => roomId !== null);
  }

  /**
   * 启动脑波数据模拟器
   * @param {Array} appointmentData - 预约数据数组
   * @param {Function} getTreatmentStatusFn - 获取治疗状态的函数
   */
  startBrainwaveSimulation(appointmentData, getTreatmentStatusFn = null) {
    if (!Array.isArray(appointmentData)) return;
    
    // 启动模拟器
    brainwaveSimulator.start();
    
    // 为每个患者初始化脑波数据
    appointmentData.forEach(appointment => {
      if (appointment && appointment.roomName) {
        const patientId = this.extractRoomId(appointment.roomName);
        const sleepState = appointment.realtime_sleep_state || 'light_sleep';
        
        // 获取治疗状态
        let treatmentStatus = '治疗中'; // 默认状态
        if (getTreatmentStatusFn && typeof getTreatmentStatusFn === 'function') {
          treatmentStatus = getTreatmentStatusFn(appointment);
        }
        
        brainwaveSimulator.initPatient(patientId, sleepState, treatmentStatus);
      }
    });
    
  }

  /**
   * 更新患者治疗状态
   * @param {string} roomId - 房间ID
   * @param {string} treatmentStatus - 治疗状态
   */
  updatePatientTreatmentStatus(roomId, treatmentStatus) {
    brainwaveSimulator.updatePatientTreatmentStatus(roomId, treatmentStatus);
  }

  /**
   * 获取模拟的脑波数据
   * @param {Array} roomIds - 房间ID列表
   * @returns {Object} 模拟的实时状态数据
   */
  getSimulatedBrainwaveData(roomIds) {
    if (!Array.isArray(roomIds)) return { data: [] };
    
    const simulatedData = [];
    
    roomIds.forEach(roomId => {
      const patientData = brainwaveSimulator.getPatientData(roomId);
      if (patientData) {
        simulatedData.push({
          room_id: roomId,
          timestamp: patientData.timestamp,
          sleep_state: patientData.sleepState,
          frequency_bands: patientData.frequency_bands,
          quality_score: Math.random() * 0.3 + 0.7, // 0.7-1.0的随机质量分数
          // 添加其他可能需要的字段
          signal_quality: 'good',
          device_status: 'connected'
        });
      }
    });
    
    return {
      success: true,
      data: simulatedData,
      timestamp: Date.now() / 1000,
      message: '模拟数据获取成功'
    };
  }

  /**
   * 更新患者睡眠状态（用于模拟器）
   * @param {string} roomId - 房间ID
   * @param {string} sleepState - 新的睡眠状态
   */
  updatePatientSleepState(roomId, sleepState) {
    brainwaveSimulator.updatePatientSleepState(roomId, sleepState);
  }

  /**
   * 停止脑波数据模拟器
   */
  stopBrainwaveSimulation() {
    brainwaveSimulator.stop();
  }

  /**
   * 获取模拟器状态
   * @returns {Object} 模拟器状态信息
   */
  getSimulatorStatus() {
    return brainwaveSimulator.getStatus();
  }
}

// 创建单例实例
const realtimeAPI = new RealtimeAPI();

export default realtimeAPI;
